"use client";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { api, parseApiError } from "@/lib/api";
import { ApiKeyHelpModal } from "@/components/ApiKeyHelpModal";

// Shown when a Tester-tier user tries to start a session (or create a patient)
// without their own Anthropic key. Tester never uses the server key, so a key
// is mandatory. Lets them paste a key inline and continue without leaving the page.
export function TesterKeyModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslations("dashboard.testerKey");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  async function handleSave() {
    if (!apiKey.trim() || saving) return;
    setSaving(true);
    setErrorMsg("");
    try {
      // Key is validated server-side before saving; an invalid one throws and
      // the modal stays open with a clear warning instead of letting the user
      // proceed into a session that would only return empty replies.
      await api.updateApiKey(apiKey.trim());
      onSaved();
    } catch (err: unknown) {
      const { code, message } = parseApiError(err);
      setErrorMsg(
        code === "invalid_api_key" ? t("invalid")
        : code === "api_key_unverifiable" ? t("unverifiable")
        : message || t("errorFallback")
      );
      setSaving(false);
    }
  }

  if (showHelp) {
    return <ApiKeyHelpModal onClose={() => setShowHelp(false)} />;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl border border-[var(--border)] shadow-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[var(--accent-light)] text-[var(--accent)] flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 0 1 21.75 8.25z" />
              </svg>
            </div>
            <h3
              className="text-lg font-semibold text-[var(--text-primary)]"
              style={{ fontFamily: "var(--font-lora)" }}
            >
              {t("title")}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors p-1 -mr-1 -mt-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
          {t("description")}
        </p>

        {/* Key input */}
        <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
          {t("inputLabel")}
          <button
            onClick={() => setShowHelp(true)}
            title={t("helpTooltip")}
            className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[var(--accent)] bg-[var(--accent-light)] text-[var(--accent)] font-bold hover:bg-[var(--accent)] hover:text-white transition-colors"
            style={{ fontSize: "9px" }}
          >
            ?
          </button>
        </label>
        <input
          type="password"
          value={apiKey}
          onChange={(e) => {
            setApiKey(e.target.value);
            if (errorMsg) setErrorMsg("");
          }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSave(); } }}
          placeholder="sk-ant-..."
          autoFocus
          className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-light)] transition-all"
        />
        <p className="mt-2 text-xs text-[var(--text-muted)] leading-relaxed">
          {t("hint")}{" "}
          <button
            onClick={() => setShowHelp(true)}
            className="text-[var(--accent)] underline underline-offset-2 hover:opacity-80"
          >
            {t("hintLink")}
          </button>
        </p>

        {errorMsg && <p className="mt-3 text-sm text-red-500">{errorMsg}</p>}

        {/* Actions */}
        <div className="mt-5 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={!apiKey.trim() || saving}
            className="rounded-xl text-white px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "var(--accent)", boxShadow: "0 2px 8px rgba(15,76,76,0.15)" }}
          >
            {saving ? t("saving") : t("saveAndContinue")}
          </button>
          <button
            onClick={onClose}
            className="rounded-xl border border-[var(--border)] px-5 py-2.5 text-sm font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            {t("cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
